<template>
  <ListViewBanner :add-route="{ name: 'app_municipality_new' }">
    Municipalities
  </ListViewBanner>

  <DefaultSearch @search="searchParams = $event" />

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th>Name</th>

        <th class="text-right w-[140px]">
          No. of Users
        </th>

        <th class="w-[120px]">
          <span class="sr-only">Actions</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!municipalities.length"
        :colspan="3"
      >
        <template v-if="municipalities.length">
          <tr
            v-for="municipality in municipalities"
            :key="municipality.id"
            class="overflow-visible"
          >
            <td>
              <RouterLink
                class="text-gray-800 text-sm"
                :to="{ name: `app_municipality_edit`, params: { municipality: municipality.id } }"
              >
                {{ municipality.name }}
              </RouterLink>

              <template v-if="municipality.archived">
                <BadgeArchived />
              </template>
            </td>

            <td class="text-right">
              {{ municipality.userCount }}
            </td>

            <td>
              <template v-if="municipality.archived">
                <RestoreButton @click="entityToRestore = municipality" />
              </template>

              <template v-else>
                <AppDropdown type="actions">
                  <AppDropdownItem
                    text="Edit"
                    :to="{ name: 'app_municipality_edit', params: { municipality: municipality.id } }"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="entityToArchive = municipality"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="entityToArchive">
    <ConfirmationModal
      title="Archive Municipality"
      intent="warning"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="entityToArchive = null"
    >
      <span class="block">Name: {{ entityToArchive?.name }}</span>
      Are you sure you want to <strong>archive</strong> this Municipality?
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="entityToRestore">
    <ConfirmationModal
      title="Restore Municipality"
      intent="confirmation"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="entityToRestore = null"
    >
      <p class="mb-0.5">
        <span>Name: {{ entityToRestore?.name }}</span>
      </p>

      <p>
        Are you sure you want to <strong>restore</strong> this Municipality?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';

import { useHttp, useEntityArchiver, useEntityRestorer } from '@/composables';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { DefaultSearch } from '@/components/forms/search';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { ConfirmationModal } from '@/components/modals';
import { RestoreButton } from '@/components/buttons';

const loading = ref();
const municipalities = ref([]);
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});
const searchParams = ref({
  page: 1,
});

const loadMunicipalities = async () => {
  loading.value = true;
  municipalities.value = [];

  const response = await useHttp().get('/municipalities', searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    municipalities.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const { archiving, entityToArchive, archiveEntity } = useEntityArchiver('municipalities', loadMunicipalities);
const { restoring, entityToRestore, restoreEntity } = useEntityRestorer('municipalities', loadMunicipalities);

watchEffect(loadMunicipalities);
</script>
